import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  CircularProgress,
  Button,
  Modal,
  Paper,
  CssBaseline,
  Grid,
  Chip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  CustomTableContainer,
  CustomTableHead,
  tableCellStyle,
} from "../../../components/TableComponent";
import { baseURL } from "../../../config";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  getCandidatesForJobAPI,
  getScreeningStatusAPI,
  getBestScreeningResult,
} from "../../../services/screeningService";

const SCREENING_LEAD_STATUS_CHIP = {
  in_progress: { label: "Pending", color: "#F59E0B" },
  qualified: { label: "Qualified", color: "#10B981" },
  "intent-qualified": { label: "Qualified (Intent)", color: "#10B981" },
  disqualified: { label: "Disqualified", color: "#EF4444" },
  "completed-RNR": { label: "No Answer", color: "#F59E0B" },
  dnd: { label: "Do Not Call", color: "#6B7280" },
  paused: { label: "Paused", color: "#6B7280" },
};

const ScreeningRankingTab = ({ jobId }) => {
  const [candidates, setCandidates] = useState([]);
  const [screeningByResumeId, setScreeningByResumeId] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!jobId) return;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getCandidatesForJobAPI(jobId);
        setCandidates(data);
        const entries = await Promise.all(
          data.map(async (c) => {
            try {
              const status = await getScreeningStatusAPI(jobId, c["Resume Id"]);
              return [c["Resume Id"], status];
            } catch (_) {
              return [c["Resume Id"], null];
            }
          })
        );
        const map = {};
        entries.forEach(([resumeId, status]) => {
          if (status) map[resumeId] = status;
        });
        setScreeningByResumeId(map);
      } catch (err) {
        console.error("Error loading screening rankings:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [jobId]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: "#6B7280" }}>
          Loading screening rankings...
        </Typography>
      </Box>
    );
  }

  const scheduled = candidates.filter((c) => screeningByResumeId[c["Resume Id"]]?.status);
  const completed = scheduled.filter((c) => {
    const results = screeningByResumeId[c["Resume Id"]]?.results || [];
    return getBestScreeningResult(results) != null;
  });
  const pending = scheduled.filter((c) => {
    const status = screeningByResumeId[c["Resume Id"]];
    return !status?.lead_status || status.lead_status === "in_progress";
  });
  const scoredValues = completed
    .map((c) => getBestScreeningResult(screeningByResumeId[c["Resume Id"]]?.results || [])?.interview_rating)
    .filter((v) => v != null);
  const avgScreeningScore = scoredValues.length
    ? scoredValues.reduce((a, b) => a + b, 0) / scoredValues.length
    : 0;

  return (
    <>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E0E3E7", bgcolor: "#FAFAFA", textAlign: "center" }}>
            <PeopleAltIcon sx={{ color: "#2563eb", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">{scheduled.length}</Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">Scheduled</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E0E3E7", bgcolor: "#FAFAFA", textAlign: "center" }}>
            <AssessmentIcon sx={{ color: "#10B981", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">{completed.length}</Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">Completed</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E0E3E7", bgcolor: "#FAFAFA", textAlign: "center" }}>
            <AssessmentIcon sx={{ color: "#F59E0B", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">{pending.length}</Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">Pending</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid #E0E3E7", bgcolor: "#FAFAFA", textAlign: "center" }}>
            <TrendingUpIcon sx={{ color: "#8B5CF6", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">{avgScreeningScore.toFixed(1)}</Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">Avg Score</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ borderRadius: "16px", border: "1px solid rgba(0, 0, 0, 0.1)", overflow: "hidden", backgroundColor: "#fff", margin: "16px 0" }}>
        <CustomTableContainer component={Paper}>
          <Table>
            <CustomTableHead style={{ position: "sticky", top: 0, backgroundColor: "#374151", zIndex: 1 }}>
              <TableRow>
                <TableCell style={{ ...tableCellStyle, minWidth: "200px", fontWeight: 600, color: "#ffffff", backgroundColor: "#374151" }}>Candidate Name</TableCell>
                <TableCell style={{ ...tableCellStyle, minWidth: "150px", fontWeight: 600, color: "#ffffff", backgroundColor: "#374151" }}>Screening Status</TableCell>
                <TableCell style={{ ...tableCellStyle, minWidth: "150px", fontWeight: 600, color: "#ffffff", backgroundColor: "#374151" }}>Screening Score</TableCell>
                <TableCell style={{ ...tableCellStyle, minWidth: "150px", fontWeight: 600, color: "#ffffff", backgroundColor: "#374151" }}>Recording</TableCell>
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {scheduled.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No screening calls scheduled for this job yet.
                  </TableCell>
                </TableRow>
              )}
              {scheduled.map((candidate) => {
                const resumeId = candidate["Resume Id"];
                const status = screeningByResumeId[resumeId];
                const latest = getBestScreeningResult(status?.results || []);
                const chip = SCREENING_LEAD_STATUS_CHIP[status?.lead_status] || SCREENING_LEAD_STATUS_CHIP.in_progress;
                return (
                  <TableRow key={resumeId} sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}>
                    <TableCell align="center" style={{ fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: "#1f2937" }}>
                      {candidate["Candidate name"]}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={chip.label} size="small" sx={{ backgroundColor: chip.color, color: "white", fontWeight: 600, fontSize: "12px" }} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontWeight: 600 }}>
                        {latest?.interview_rating != null ? Number(latest.interview_rating).toFixed(1) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {latest?.call_recording_link ? (
                        <a
                          href={latest.call_recording_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#2563eb", textDecoration: "underline" }}
                        >
                          Recording
                        </a>
                      ) : (
                        <Typography sx={{ color: "#6B7280", fontStyle: "italic" }}>N/A</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CustomTableContainer>
      </Box>
    </>
  );
};

const Ranking = ({ job }) => {
  const [rankings, setRankings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryModal, setSummaryModal] = useState({
    open: false,
    content: "",
  });
  // Screening opens by default (tab value 1); Interview is 0.
  const [activeTab, setActiveTab] = useState(1);
  const { jobId } = useParams();

  // Fetch Rankings
  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const response = await axios.get(`${baseURL}interview/${jobId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        });

        // Sort rankings by Total Rank; if Total Rank is null, sort by Total Resume Score
        const sortedRankings = response.data["Total Scores"].sort((a, b) => {
          const rankA = a["Total Rank"] !== null ? a["Total Rank"] : Infinity;
          const rankB = b["Total Rank"] !== null ? b["Total Rank"] : Infinity;

          // If Total Rank is equal, fall back to Total Resume Score
          if (rankA === rankB) {
            const resumeScoreA = a["Total Resume score"] || 0;
            const resumeScoreB = b["Total Resume score"] || 0;
            return resumeScoreB - resumeScoreA; // Higher Resume Score comes first
          }

          return rankA - rankB; // Lower Total Rank comes first
        });

        setRankings({ ...response.data, "Total Scores": sortedRankings });
        setLoading(false);

        // Fetch Zoom Interview Details after Rankings are loaded
        fetchZoomDetails(sortedRankings);
      } catch (err) {
        console.error("Error fetching rankings:", err);
        setError("Rankings not available.");
        setLoading(false);
      }
    };

    const fetchZoomDetails = async (sortedRankings) => {
      try {
        const zoomResponse = await axios.get(
          `${baseURL}zoom-interview-score/${jobId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        // Map Zoom interview details by Resume Id
        const zoomDetailsMap = {};
        zoomResponse.data.results.forEach((detail) => {
          zoomDetailsMap[detail.resume_id] = detail;
        });

        // Merge Zoom details into the rankings
        const updatedRankings = sortedRankings.map((candidate) => ({
          ...candidate,
          zoomDetails: zoomDetailsMap[candidate["Resume Id"]] || null,
        }));

        setRankings((prev) => ({ ...prev, "Total Scores": updatedRankings }));
      } catch (err) {
        console.warn("Zoom interview details not available:", err);
        // Proceed without Zoom details
      }
    };

    fetchRankings();
  }, [jobId]);

  const openSummaryModal = (content) => {
    setSummaryModal({ open: true, content });
  };

  const closeSummaryModal = () => {
    setSummaryModal({ open: false, content: "" });
  };


  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "#10B981";
      case "scheduled":
        return "#3B82F6";
      case "pending":
        return "#F59E0B";
      default:
        return "#6B7280";
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
        p={4}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: "#6B7280" }}>
          Loading rankings...
        </Typography>
      </Box>
    );
  }

  if (!jobId) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
      >
        <CssBaseline />
        <Typography variant="h5" sx={{ color: "#6B7280" }}>
          Select a job from Job Management Section
        </Typography>
      </Box>
    );
  }

  // Interview rankings require InterviewScore rows (only exist once someone's been
  // through interview selection) — that emptiness must not hide the Screening tab,
  // which has its own independent data source and default-opens regardless.
  const hasInterviewRankings = Boolean(
    rankings && rankings["Total Scores"] && rankings["Total Scores"].length > 0
  );

  const totalCandidates = hasInterviewRankings ? rankings["Total Scores"].length : 0;
  const completedInterviews = hasInterviewRankings
    ? rankings["Total Scores"].filter(
        (candidate) => candidate["Interview Status"]?.toLowerCase() === "completed"
      ).length
    : 0;
  const pendingInterviews = hasInterviewRankings
    ? rankings["Total Scores"].filter(
        (candidate) => candidate["Interview Status"]?.toLowerCase() === "pending"
      ).length
    : 0;
  const scheduledInterviews = hasInterviewRankings
    ? rankings["Total Scores"].filter(
        (candidate) => candidate["Interview Status"]?.toLowerCase() === "scheduled"
      ).length
    : 0;
  const averageResumeScore = hasInterviewRankings
    ? rankings["Total Scores"].reduce(
        (acc, candidate) => acc + (candidate["Total Resume score"] || 0),
        0
      ) / totalCandidates
    : 0;

  return (
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.2)",
        borderRadius: "30px",
        background: "#FFFFFF",
        boxShadow: "6px 6px 54px 0px #0000000D",
        padding: "20px",
      }}
    >
      {/* Header Section */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={3}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ 
              fontWeight: 700, 
              color: "#1f2937", 
              mb: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 1
            }}
          >
            Candidate Rankings
          </Typography>
        </Box>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3, borderBottom: "1px solid #e5e7eb" }}
      >
        <Tab label="Interview" value={0} />
        <Tab label="Screening" value={1} />
      </Tabs>

      {activeTab === 1 && <ScreeningRankingTab jobId={jobId} />}

      {activeTab === 0 && !hasInterviewRankings && (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="200px">
          <Typography variant="h6" sx={{ color: "#6B7280" }}>
            No interview rankings available for this job yet.
          </Typography>
        </Box>
      )}

      {activeTab === 0 && hasInterviewRankings && (
        <>
      {/* Statistics Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #E0E3E7",
              bgcolor: "#FAFAFA",
              textAlign: "center",
            }}
          >
            <PeopleAltIcon sx={{ color: "#2563eb", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">
              {totalCandidates}
            </Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">
              Total Candidates
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #E0E3E7",
              bgcolor: "#FAFAFA",
              textAlign: "center",
            }}
          >
            <AssessmentIcon sx={{ color: "#10B981", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">
              {completedInterviews}
            </Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">
              Completed
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #E0E3E7",
              bgcolor: "#FAFAFA",
              textAlign: "center",
            }}
          >
            <AssessmentIcon sx={{ color: "#3B82F6", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">
              {scheduledInterviews}
            </Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">
              Scheduled
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #E0E3E7",
              bgcolor: "#FAFAFA",
              textAlign: "center",
            }}
          >
            <AssessmentIcon sx={{ color: "#F59E0B", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">
              {pendingInterviews}
            </Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">
              Pending
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: "1px solid #E0E3E7",
              bgcolor: "#FAFAFA",
              textAlign: "center",
            }}
          >
            <TrendingUpIcon sx={{ color: "#8B5CF6", fontSize: 32, mb: 1 }} />
            <Typography variant="h5" fontWeight={700} color="#1f2937">
              {averageResumeScore.toFixed(1)}
            </Typography>
            <Typography variant="body2" color="#6b7280" fontSize="12px">
              Avg Score
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Table Section */}
      <Box
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          backgroundColor: "#fff",
          margin: "16px 0",
        }}
      >
        <CustomTableContainer component={Paper}>
          <Table>
            <CustomTableHead
              style={{
                position: "sticky",
                top: 0,
                backgroundColor: "#374151",
                zIndex: 1,
              }}
            >
              <TableRow>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "200px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Candidate Name
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "150px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Interview Status
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "150px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Resume Score
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "150px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Interview Score
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "150px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Zoom Score
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "120px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Total Rank
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "140px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Body Language
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "120px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Confidence
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "140px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Engagement
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "140px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Eye Contact
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "150px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Leadership
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "120px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Vocabulary
                </TableCell>
                <TableCell
                  style={{
                    ...tableCellStyle,
                    minWidth: "120px",
                    fontWeight: 600,
                    color: "#ffffff",
                    backgroundColor: "#374151",
                  }}
                >
                  Summary
                </TableCell>
              </TableRow>
            </CustomTableHead>
            <TableBody>
              {rankings["Total Scores"].map((candidate) => {
                const zoomDetails = candidate.zoomDetails;
                const resumeScore = candidate["Total Resume score"] || 0;
                const interviewScore = candidate["Total Interview score"] || 0;
                const zoomScore = zoomDetails?.overall_score || 0;
                
                return (
                  <TableRow 
                    key={candidate["Resume Id"]}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                      },
                    }}
                  >
                    <TableCell align="center"
                      style={{
                        fontFamily: "Montserrat",
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#1f2937",
                        
                      }}
                    >
                      {candidate["Candidate name"]}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={candidate["Interview Status"] || "N/A"}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(candidate["Interview Status"]),
                          color: "white",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: getScoreColor(resumeScore),
                          fontWeight: 600,
                        }}
                      >
                        {resumeScore.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: interviewScore > 0 ? getScoreColor(interviewScore) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {interviewScore > 0 ? interviewScore.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: zoomScore > 0 ? getScoreColor(zoomScore) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {zoomScore > 0 ? zoomScore.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={candidate["Total Rank"] || "Not Ranked"}
                        size="small"
                        sx={{
                          backgroundColor: candidate["Total Rank"] ? "#2563eb" : "#6B7280",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "12px",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: zoomDetails?.body_language ? getScoreColor(zoomDetails.body_language) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {zoomDetails?.body_language ? zoomDetails.body_language.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: zoomDetails?.confidence ? getScoreColor(zoomDetails.confidence) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {zoomDetails?.confidence ? zoomDetails.confidence.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: zoomDetails?.engagement_with_interviewers ? getScoreColor(zoomDetails.engagement_with_interviewers) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {zoomDetails?.engagement_with_interviewers ? zoomDetails.engagement_with_interviewers.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: zoomDetails?.eye_contact_with_interviewers ? getScoreColor(zoomDetails.eye_contact_with_interviewers) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {zoomDetails?.eye_contact_with_interviewers ? zoomDetails.eye_contact_with_interviewers.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: zoomDetails?.leadership_traits ? getScoreColor(zoomDetails.leadership_traits) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {zoomDetails?.leadership_traits ? zoomDetails.leadership_traits.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        sx={{
                          // color: zoomDetails?.vocabulary_and_grammar ? getScoreColor(zoomDetails.vocabulary_and_grammar) : "#6B7280",
                          fontWeight: 600,
                        }}
                      >
                        {zoomDetails?.vocabulary_and_grammar ? zoomDetails.vocabulary_and_grammar.toFixed(2) : "N/A"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {zoomDetails && zoomDetails.interview_summary ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            openSummaryModal(zoomDetails.interview_summary)
                          }
                          sx={{
                            borderRadius: "8px",
                            textTransform: "none",
                            borderColor: "#2563eb",
                            color: "#2563eb",
                            fontWeight: 600,
                            "&:hover": {
                              borderColor: "#1d4ed8",
                              backgroundColor: "#eff6ff",
                            },
                          }}
                        >
                          View Summary
                        </Button>
                      ) : (
                        <Typography sx={{ color: "#6B7280", fontStyle: "italic" }}>
                          N/A
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CustomTableContainer>
      </Box>
        </>
      )}

      {/* Enhanced Summary Modal */}
      <Modal open={summaryModal.open} onClose={closeSummaryModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: "800px",
            maxHeight: "80vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 24,
            p: 4,
            overflow: "auto",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight={700} color="#1f2937">
              Interview Summary
            </Typography>
            <Button
              onClick={closeSummaryModal}
              sx={{
                color: "#6B7280",
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              ✕
            </Button>
          </Box>
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "#f8fafc",
              border: "1px solid #e5e7eb",
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                lineHeight: 1.6,
                color: "#374151",
                whiteSpace: "pre-wrap",
              }}
            >
              {summaryModal.content}
            </Typography>
          </Box>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              variant="contained"
              onClick={closeSummaryModal}
              sx={{
                backgroundColor: "#2563eb",
                "&:hover": {
                  backgroundColor: "#1d4ed8",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Ranking;
